import { useEffect } from 'react'
import 'swiper/css'
import { Button } from '~/components/common/Button'
import { LineWobble } from '~/components/common/LineWobble'
import { ConnectionType } from '~/constants/connections'
import { useAzureDevOpsOrganization } from '~/hooks/useAzureDevOpsOrganization'
import { useRepositoryBranches } from '~/hooks/useRepositoryBranches'
import { useTracer } from '~/hooks/useTracer'
import { bridge } from '~/services/bridge'
import { AppEvents, globalEventEmitter } from '~/utils/shared'
import { BranchSelectionForm } from './BranchSelectionForm'
import styles from '../settings.module.scss'

interface AuthorizeBranchModalProps {
  modalOpenChange: () => void
  connType: ConnectionType
}

export default function AuthorizeBranchForm({
  modalOpenChange,
  connType
}: AuthorizeBranchModalProps) {
  const traceWithUserInfo = useTracer()

  const isAzureDevOps = connType === ConnectionType.AzureDevOps

  // Use extracted hooks
  const {
    orgState: azureOrgState,
    loadOrganizations,
    selectOrganization,
    showOrgSelector,
    resetForNonAzure
  } = useAzureDevOpsOrganization()

  // Optimized: loadRepositoriesAndBranches fetches repos and branches in a single API call
  const {
    loadRepositoriesAndBranches,
    submitRepositories,
    isLoading,
    isSubmitting,
    repositoryBranches,
    setRepositoryBranches
  } = useRepositoryBranches()

  // Initialize data on component mount
  useEffect(() => {
    const initializeData = async () => {
      try {
        // Validate connType before proceeding
        if (!connType) {
          console.error('ConnType is not provided')
          return
        }

        // For Azure DevOps, first load available organizations
        if (connType === ConnectionType.AzureDevOps) {
          const shouldShowSelection = await loadOrganizations()

          if (shouldShowSelection) {
            return // Don't load repos yet, wait for org selection
          }
        } else {
          // Reset Azure DevOps state for non-Azure DevOps connections
          resetForNonAzure()
        }

        // Load repositories and branches
        await loadRepositoriesAndBranches(connType)
      } catch (error) {
        console.error('AuthorizeBranchForm: Error initializing data:', error)
      }
    }

    initializeData()
  }, [connType, loadOrganizations, loadRepositoriesAndBranches, resetForNonAzure])

  const handleOrgSelection = async (orgName: string) => {
    const success = await selectOrganization(orgName)
    if (success) {
      // Load repositories for the selected organization
      await loadRepositoriesAndBranches(connType)
    }
  }

  const onSubmit = async () => {
    const success = await submitRepositories(connType, traceWithUserInfo)
    if (success) {
      globalEventEmitter.emit(AppEvents.IndexesUpdated)
      modalOpenChange()
    }
  }

  const OrganizationSelector = () => (
    <div className='p-6'>
      <div className='mb-4'>
        <h3 className='text-lg font-semibold text-gray-900 mb-2'>
          Select Azure DevOps Organization
        </h3>
        <p className='text-sm text-gray-600 mb-4'>
          Choose which Azure DevOps organization you want to connect to:
        </p>
      </div>

      <div className='space-y-3'>
        {azureOrgState.available.map(orgName => (
          <div
            key={orgName}
            className={`p-4 border rounded-lg cursor-pointer transition-colors ${
              azureOrgState.selected === orgName
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
            onClick={() => handleOrgSelection(orgName)}
          >
            <div className='flex items-center justify-between'>
              <div>
                <div className='font-medium text-gray-900'>{orgName}</div>
                {azureOrgState.selected === orgName && (
                  <div className='text-sm text-blue-600 mt-1'>Currently selected</div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  const OrganizationHeader = () => (
    <div className='px-6 py-3 bg-blue-50 border-b border-blue-100'>
      <div className='flex items-center justify-between'>
        <div className='text-sm text-blue-800'>
          <span className='font-medium'>Organization:</span> {azureOrgState.selected}
        </div>
        <button
          onClick={showOrgSelector}
          className='text-sm text-blue-600 hover:text-blue-800 underline'
        >
          Switch Organization
        </button>
      </div>
    </div>
  )

  const isPlugin = bridge.isInPlugin()

  return (
    <div className='flex flex-col flex-1 min-h-0'>
      <div
        className={`flex-1 min-h-0 w-full overflow-y-auto ${styles['modal-scrollbar-container']} ${
          isPlugin ? 'pt-4 px-4' : 'pt-12 px-[120px]'
        }`}
      >
        <div className='w-full'>
          {isLoading || (isAzureDevOps && azureOrgState.isLoading) ? (
            <div className='flex justify-center items-center min-h-[200px]'>
              <div className='flex justify-center items-center h-32 flex-col'>
                <LineWobble />
                <div className='text-gray-500 mt-2'>
                  Loading {isAzureDevOps ? 'organizations and repositories' : 'repositories'}...
                </div>
              </div>
            </div>
          ) : (
            <>
              {isAzureDevOps && azureOrgState.showSelection && <OrganizationSelector />}
              <div
                style={{
                  display: isAzureDevOps && azureOrgState.showSelection ? 'none' : 'block'
                }}
              >
                {isAzureDevOps && azureOrgState.selected && <OrganizationHeader />}
                <BranchSelectionForm
                  key={`${connType}-${azureOrgState.selected}-${repositoryBranches.length}`}
                  repos={repositoryBranches}
                  setRepos={setRepositoryBranches}
                  serverName={connType || ''}
                />
              </div>
            </>
          )}
        </div>
      </div>
      <div
        className='
        w-full shrink-0 h-[68px] py-[16px] px-[32px] flex items-center
       bg-tokens-neutral-bg-grey-1 border-t border-tokens-neutral-border-2
       justify-end gap-2
      '
      >
        <Button variant='outlined' onClick={modalOpenChange} className='w-[78px] h-9'>
          Cancel
        </Button>
        <Button
          variant='primary'
          className='w-[65px] h-9'
          isDisabled={
            isLoading ||
            isSubmitting ||
            (isAzureDevOps && (azureOrgState.isLoading || azureOrgState.showSelection))
          }
          onClick={onSubmit}
        >
          Save
        </Button>
      </div>
    </div>
  )
}
